import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Chip } from "primereact/chip";
import CircularProgress from "@mui/material/CircularProgress";

/**
 * Repository files DataTable wrapper
 * @param {Object} props
 * @param {Array} props.data - Array of file data
 * @param {Function} props.onDelete - Callback when delete is clicked
 * @param {Object} props.filters - PrimeReact filter state (optional)
 * @param {string} props.emptyMessage - Custom empty state message (optional)
 * @param {Object} props.tableProps - Additional props to pass to DataTable
 */
const RepositoryTable = ({
  data,
  onDelete,
  filters: customFilters,
  emptyMessage,
  ...tableProps
}) => {
  const defaultFilters = {
    Name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    Category: { value: null, matchMode: FilterMatchMode.CONTAINS },
    Username: { value: null, matchMode: FilterMatchMode.CONTAINS },
    DateTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };

  const filters = customFilters || defaultFilters;

  const dateTemplate = (rowData) => {
    const dateTime = rowData["DateTime"];
    if (!dateTime) return <div>N/A</div>;

    const date = new Date(dateTime);
    const formattedDate = date.toLocaleDateString();
    const formattedTime = date.toLocaleTimeString();

    return (
      <div>
        <div>{formattedDate}</div>
        <div>{formattedTime}</div>
      </div>
    );
  };

  const deleteTemplate = (rowData) => (
    <button
      type="button"
      className="repository-delete-btn"
      aria-label="Delete row"
      onClick={() => onDelete(rowData)}
    >
      <img
        src="./assets/img/reports/delete-repo.svg"
        alt="Delete Icon"
        className="actions-icon"
      />
    </button>
  );

  const statusTemplate = (rowData) => {
    const status = rowData.Status || "";
    const isProcessing = status.toLowerCase().includes("analyzing");
    const isComplete = status.toLowerCase().includes("complete");
    const isError = status.toLowerCase().includes("error");

    if (isProcessing) {
      return (
        <Chip
          label={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <CircularProgress size={12} thickness={5} sx={{ color: "#666" }} />
              Processing
            </span>
          }
          style={{
            backgroundColor: "#fff3e0",
            color: "#e65100",
            fontSize: "12px",
            height: "28px",
          }}
        />
      );
    }

    if (isComplete) {
      return (
        <Chip
          label="Ready"
          icon={<span style={{ marginLeft: "8px" }}>&#10003;</span>}
          style={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontSize: "12px",
            height: "28px",
          }}
        />
      );
    }

    if (isError) {
      return (
        <Chip
          label="Error"
          icon={<span style={{ marginLeft: "8px" }}>&#10007;</span>}
          style={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            fontSize: "12px",
            height: "28px",
          }}
        />
      );
    }

    return (
      <Chip
        label="Pending"
        style={{
          backgroundColor: "#f5f5f5",
          color: "#666",
          fontSize: "12px",
          height: "28px",
        }}
      />
    );
  };

  const defaultEmptyMessage = (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <p style={{ color: "#666", marginBottom: "0" }}>
        No records added.
      </p>
    </div>
  );

  return (
    <DataTable
      className="repository-table"
      value={data}
      paginator
      rows={10}
      rowsPerPageOptions={[10, 20, 25, 50]}
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      filters={filters}
      filterDisplay="row"
      globalFilterFields={["Name", "Category", "Username", "DateTime"]}
      emptyMessage={emptyMessage || defaultEmptyMessage}
      {...tableProps}
    >
      <Column
        header="File Name"
        field="Name"
        sortable
        filter
        filterPlaceholder="Filter"
        style={{ minWidth: "200px" }}
      />
      <Column
        header="Category"
        field="Category"
        sortable
        filter
        filterPlaceholder="Filter"
        style={{ width: "180px" }}
      />
      <Column
        header="User Name"
        field="Username"
        sortable
        filter
        filterPlaceholder="Filter"
        style={{ width: "150px" }}
      />
      <Column
        header="Date"
        field="DateTime"
        body={dateTemplate}
        sortable
        filter
        filterPlaceholder="Filter"
        style={{ width: "200px" }}
      />
      <Column
        header="AI Status"
        field="Status"
        body={statusTemplate}
        sortable
        style={{ width: "130px", textAlign: "center" }}
      />
      <Column
        header=""
        body={deleteTemplate}
        style={{ width: "60px", textAlign: "center" }}
      />
    </DataTable>
  );
};

export default RepositoryTable;
